import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Card, Table, Tag, Space, Select, Input } from 'antd';
import { PlusOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useQuery } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { ticketsApi } from '../../services/api';

interface Ticket {
  id: string;
  subject: string;
  status: string;
  priority: string;
  category?: string;
  created_at: string;
  updated_at: string;
}

const statusColors: Record<string, string> = {
  open: 'blue',
  in_progress: 'orange',
  resolved: 'green',
  closed: 'default',
};

const priorityColors: Record<string, string> = {
  low: 'default',
  medium: 'blue',
  high: 'orange',
  urgent: 'red',
};

export default function SupportPage() {
  const { t } = useTranslation('support');
  const navigate = useNavigate();
  const [status, setStatus] = useState<string>('');
  const [searchText, setSearchText] = useState('');

  const { data, isLoading } = useQuery({
    queryKey: ['tickets', status],
    queryFn: () => ticketsApi.getMyTickets({ status: status || undefined }),
  });

  const tickets = data?.data?.items || [];

  const columns: ColumnsType<Ticket> = [
    {
      title: t('columns.subject'),
      dataIndex: 'subject',
      key: 'subject',
      render: (text: string, record: Ticket) => (
        <a onClick={() => navigate(`/support/${record.id}`)}>{text}</a>
      ),
    },
    {
      title: t('columns.status'),
      dataIndex: 'status',
      key: 'status',
      width: 100,
      render: (status: string) => (
        <Tag color={statusColors[status]}>{t(`status.${status}`, status)}</Tag>
      ),
    },
    {
      title: t('columns.priority'),
      dataIndex: 'priority',
      key: 'priority',
      width: 100,
      render: (priority: string) => (
        <Tag color={priorityColors[priority]}>{t(`priority.${priority}`, priority)}</Tag>
      ),
    },
    {
      title: t('columns.category'),
      dataIndex: 'category',
      key: 'category',
      width: 120,
      render: (category: string) => category || '-',
    },
    {
      title: t('columns.created_at'),
      dataIndex: 'created_at',
      key: 'created_at',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString(),
    },
  ];

  const filteredTickets = tickets.filter((ticket: Ticket) =>
    ticket.subject.toLowerCase().includes(searchText.toLowerCase())
  );

  return (
    <div>
      <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <h1 style={{ margin: 0, fontSize: 24, fontWeight: 600 }}>{t('title')}</h1>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => navigate('/support/new')}>
          {t('create_ticket')}
        </Button>
      </div>

      <Card>
        <Space style={{ marginBottom: 16, width: '100%' }} direction="vertical">
          <Space>
            <Input
              placeholder={t('search_placeholder')}
              prefix={<SearchOutlined />}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              style={{ width: 300 }}
            />
            <Select
              placeholder={t('filter_status')}
              value={status}
              onChange={setStatus}
              style={{ width: 150 }}
              allowClear
            >
              <Select.Option value="">{t('all_status')}</Select.Option>
              <Select.Option value="open">{t('status.open')}</Select.Option>
              <Select.Option value="in_progress">{t('status.in_progress')}</Select.Option>
              <Select.Option value="resolved">{t('status.resolved')}</Select.Option>
              <Select.Option value="closed">{t('status.closed')}</Select.Option>
            </Select>
          </Space>
        </Space>

        <Table
          columns={columns}
          dataSource={filteredTickets}
          rowKey="id"
          loading={isLoading}
          pagination={{
            pageSize: 10,
            showTotal: (total) => t('total_count', { count: total }),
          }}
        />
      </Card>
    </div>
  );
}
